import { ref, computed, nextTick, inject } from 'vue'
import { truthyClassesToString, isObject, hasOwn } from '../utils/helpers'
import { uuid as createUUID } from '../utils/helpers'

export default function validationMixin(context) {
  const isPristine = ref(true)
  const isValid = ref(true)
  const uuid = ref(createUUID())
  const form = inject('akForm', null)
  const field = inject('akField', null)
  let group

  // WE are going to look for this property on the props object, since
  // if we find this then we will have a checkbox/radio group component
  // as a parent and we want to use those validations instead
  if (context.props.isGroup) {
    group = inject(
      context.type === 'checkbox' ? 'akCheckboxGroup' : 'akRadioGroup'
    )
  }

  /**
   * @returns {Boolean}
   * @description Returns a boolean flag to determine if the input is
   * a checkbox or radio group, we are going to treat these as a
   * single input
   */
  const isCheckRadioGroup = computed(() => {
    return context.props.isGroup && isObject(group)
  })

  /**
   * @return {String}
   * @description Get the type prop from parent if it's a Field.
   */
  const statusType = () => {
    if (!field) return
    if (!field.newType) return
    if (typeof field.newType === 'string') {
      return field.newType
    } else {
      for (let key in field.newType) {
        if (field.newType[key]) {
          return key
        }
      }
    }
  }

  /**
   * @returns {String}
   * @description Returns the error classes for the input
   */
  const inputErrorClass = computed(() => {
    if (field) {
      return truthyClassesToString(
        field.newType,
        'ak-input__control--validation-'
      )
    } else {
      return ''
    }
  })

  /**
   * @returns {String}
   * @description Returns the icon error classes for the input
   */
  const iconErrorClass = computed(() => {
    if (field) {
      return truthyClassesToString(field.newType, 'text-')
    } else {
      return ''
    }
  })

  /**
   * @description Invalidates the input and sets the validation error
   * message.
   */
  const setInvalid = () => {
    let type = 'danger'
    let message = context.input.value.validationMessage
    setValidity(type, message)
  }

  /**
   * @param {String} message
   * @param {Object} parent
   * @description Sets the field type into the field
   */
  const setFieldType = (type, parent) => {
    /* istanbul ignore else */
    if (isObject(parent) && typeof parent.setType === 'function') {
      parent.setType(type)
    }
  }

  /**
   * @param {String} message
   * @param {Object} parent
   * @description Sets the error message into the field
   */
  const setFieldMessage = (message, parent) => {
    /* istanbul ignore else */
    if (isObject(parent) && typeof parent.setMessage === 'function') {
      parent.setMessage(message)
    }
  }

  /**
   * @param {String} type
   * @param {String} message
   * @description Sets the validity state of the input
   */
  const setValidity = (type, message) => {
    nextTick(() => {
      setFieldMessage(message, field)
      setFieldType(type, field)
    })
  }

  const setPristine = (bool) => {
    if (typeof bool === 'boolean') {
      isPristine.value = bool
    }
  }

  /**
   * @returns {Promise}
   * @description Executes any custom rules that may have been applied to
   * the input, these can be sync or async functions and will pass the
   * HTMLElement input into the rule function this is so we can read
   * the value and set custom validation rules.
   */
  const checkCustomRules = async () => {
    const { customRules } = context.props
    const input = context.input.value
    if (hasOwn(context, 'value')) {
      input.value = context.value.value
    }
    const results = []
    if (Array.isArray(customRules) && customRules.length > 0) {
      for (const rule of customRules) {
        const result = await rule(input)
        results.push(result)
      }
    }
    return !results.some((i) => i === false)
  }

  /**
   * @param {Boolean} forceDisplay
   * @description Check HTML5 validation, set isValid property. If validation
   * fail, send 'danger' type, and error message to parent if it's a Field.
   */
  const validate = async (forceDisplay = false) => {
    /* istanbul ignore next */
    if (!context.props.useHtml5Validation) return

    if (isCheckRadioGroup.value) {
      return group.validate(forceDisplay)
    }

    /* istanbul ignore next */
    if (context.input.value === undefined) return
    if (forceDisplay) setPristine(false)

    await checkCustomRules(context)

    if (
      context.input.value &&
      typeof context.input.value.checkValidity === 'function' &&
      !context.input.value.checkValidity()
    ) {
      if (!isPristine.value) setInvalid()
      isValid.value = false
    } else {
      setValidity(null, null)
      isValid.value = true
    }

    return isValid.value
  }

  /**
   * @description Callback for the "beforeUnmount" lifecycle events in vue
   * this will be executed before the component is removed
   */
  const beforeUnmount = () => {
    /* istanbul ignore else */
    if (form) {
      if (!isCheckRadioGroup.value) {
        if (typeof form.unregister === 'function') {
          form.unregister(inst.uuid.value)
        }
      }
    }
  }

  const inst = {
    validate,
    setInvalid,
    isPristine,
    setPristine,
    isValid,
    uuid,
    inputErrorClass,
    iconErrorClass,
    isCheckRadioGroup,
    statusType,
    beforeUnmount,
  }

  if (form) {
    if (!isCheckRadioGroup.value) {
      if (typeof form.register === 'function') {
        form.register(inst)
      }
    }
  }

  return inst
}
